import React, {
  useRef,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Helmet } from "react-helmet-async";
import Sidenav from "../components/Sidenav";
import BlestFiltersModal from "../components/BlestFiltersModal";
import "../Console.css";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const IRIS_KEY = "f87f030d-eaf4-4a39-9d5d-c9ffdb90ab43";

const BlestStatementOfAccounts = ({ domain, userData }) => {
  const statementRef = useRef();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [filterTagType, setFilterTagType] = useState("");
  const [filterClient, setFilterClient] = useState("");

  const [allOrgs, setAllOrgs] = useState([]);

  useEffect(() => {
    const handleContextMenu = (e) => e.preventDefault();
    window.addEventListener("contextmenu", handleContextMenu);
    return () => window.removeEventListener("contextmenu", handleContextMenu);
  }, []);

  const userType = useMemo(
    () => (userData?.[1] || "").toString().toLowerCase().trim(),
    [userData],
  );
  const clientIdFromUser = useMemo(
    () => (userData?.[5] || "").toString().trim(),
    [userData],
  );
  const isBrandAdmin = userType === "brandadmin";

  useEffect(() => {
    fetch("/api/organizations")
      .then((r) => r.json())
      .then((data) => setAllOrgs(data || []))
      .catch(() => {});
  }, []);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const url =
        isBrandAdmin && clientIdFromUser
          ? `https://api.iriscrypt.com/orderAssignment/by-client/${encodeURIComponent(clientIdFromUser)}`
          : "https://api.iriscrypt.com/orderAssignment";

      const response = await fetch(url, {
        headers: { "x-api-key": IRIS_KEY },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const result = await response.json();
      const sorted = [...(result || [])].sort(
        (a, b) => new Date(b.date) - new Date(a.date),
      );
      setOrders(sorted);
    } catch (error) {
      console.error("Failed to fetch order assignments:", error);
      setErr("Failed to load statement data.");
    } finally {
      setLoading(false);
    }
  }, [isBrandAdmin, clientIdFromUser]);

  useEffect(() => {
    const userReady = Array.isArray(userData) && userData.length >= 6;
    if (!userReady) return;
    if (isBrandAdmin && !clientIdFromUser) return;
    fetchOrders();
  }, [userData, isBrandAdmin, clientIdFromUser, fetchOrders]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const allTagTypes = useMemo(() => {
    const types = new Set(orders.map((o) => o.type).filter(Boolean));
    return Array.from(types).sort();
  }, [orders]);

  const allClients = useMemo(() => {
    const names = new Set(orders.map((o) => o.client?.name).filter(Boolean));
    return Array.from(names).sort();
  }, [orders]);

  // The client name in scope for the current view:
  // - brandAdmin: always their own client (from IRIS response)
  // - non-brandAdmin: explicit client filter, or the single unique client if only one exists
  const effectiveClientName = useMemo(() => {
    if (isBrandAdmin) return orders[0]?.client?.name || null;
    if (filterClient) return filterClient;
    if (allClients.length === 1) return allClients[0];
    return null;
  }, [isBrandAdmin, orders, filterClient, allClients]);

  // Resolve the org record for the client currently in scope
  const clientOrgDetails = useMemo(() => {
    if (!allOrgs.length || !effectiveClientName) return null;
    if (isBrandAdmin && clientIdFromUser) {
      return allOrgs.find((o) => o._id === clientIdFromUser) || null;
    }
    return allOrgs.find((o) => o.organization === effectiveClientName) || null;
  }, [allOrgs, isBrandAdmin, clientIdFromUser, effectiveClientName]);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      if (dateFrom && new Date(order.date) < new Date(dateFrom)) return false;
      if (dateTo) {
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (new Date(order.date) > toDate) return false;
      }
      if (
        filterTagType &&
        order.type?.toLowerCase() !== filterTagType.toLowerCase()
      )
        return false;
      if (filterClient && order.client?.name !== filterClient) return false;
      return true;
    });
  }, [orders, dateFrom, dateTo, filterTagType, filterClient]);

  const totalTags = useMemo(
    () =>
      filteredOrders.reduce((s, o) => s + (o.maxSerial - o.minSerial + 1), 0),
    [filteredOrders],
  );

  const totalAssignments = useMemo(
    () =>
      filteredOrders.reduce((s, o) => s + (o.tagAssignments?.length || 0), 0),
    [filteredOrders],
  );

  const [showFiltersModal, setShowFiltersModal] = useState(false);

  const hasFilters = dateFrom || dateTo || filterTagType || filterClient;

  const activeFilterCount = [dateFrom, dateTo, filterTagType, filterClient].filter(Boolean).length;

  const clearFilters = () => {
    setDateFrom("");
    setDateTo("");
    setFilterTagType("");
    setFilterClient("");
  };

  const handleApplyFilters = ({ dateFrom: f, dateTo: t, filterTagType: tt, filterClient: fc }) => {
    setDateFrom(f);
    setDateTo(t);
    setFilterTagType(tt);
    setFilterClient(fc);
  };

  const handleDownload = async () => {
    if (!statementRef.current || !filteredOrders.length) return;
    const canvas = await html2canvas(statementRef.current, {
      scale: 2,
      useCORS: true,
    });
    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, "PNG", 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    pdf.save("blest-statement-of-accounts.pdf");
  };

  const totalsByCurrency = useMemo(() => {
    const map = {};
    filteredOrders.forEach((o) => {
      if (!o.currency || o.totalCost == null) return;
      map[o.currency] = (map[o.currency] || 0) + o.totalCost;
    });
    return map;
  }, [filteredOrders]);

  const fmtNum = (n) =>
    Number.isFinite(n)
      ? n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "0.00";

  const cell = { border: "1px solid #ddd", padding: "6px", fontSize: "9pt" };
  const cellR = { ...cell, textAlign: "right" };

  return (
    <>
      <Helmet>
        <title>Statement of Accounts | BleST</title>
      </Helmet>

      <div className="d-flex">
        <Sidenav domain={domain} />
        <div
          style={{
            flex: 1,
            position: "relative",
            height: "calc(100vh - 94px)",
            overflowY: "auto",
          }}
        >
          {/* Header */}
          <header
            style={{
              position: "relative",
              paddingInline: "30px",
              paddingBlock: "30px",
              color: "#fff",
              background: "linear-gradient(135deg, rgba(255,81,0,1) 0%, rgba(255,153,1,1) 100%)",
              overflow: "hidden",
            }}
          >
            <div
              aria-hidden
              style={{
                position: "absolute", inset: 0,
                backgroundImage: "url('/images/background-art.jpg')",
                backgroundRepeat: "repeat", backgroundSize: "400px 400px",
                backgroundPosition: "center", mixBlendMode: "overlay",
                opacity: 0.08, zIndex: 0, pointerEvents: "none",
              }}
            />
            <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <button
                  onClick={() => window.history.back()}
                  style={{
                    display: "flex", alignItems: "center", gap: 7,
                    background: "rgba(255,255,255,0.18)", border: "1.5px solid rgba(255,255,255,0.35)",
                    color: "#fff", borderRadius: 10, padding: "8px 16px",
                    fontWeight: 700, fontSize: 13, cursor: "pointer",
                    backdropFilter: "blur(4px)", flexShrink: 0,
                  }}
                >
                  <i className="fa-solid fa-arrow-left" style={{ fontSize: 12 }} />
                  Back
                </button>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                    <i className="fa-solid fa-file-invoice-dollar" style={{ fontSize: 22, filter: "drop-shadow(1px 2px 0px #1c1c1c)" }} />
                    <b style={{ fontSize: 26, textShadow: "#1c1c1c 1px 2px 0px", lineHeight: 1.1 }}>
                      Statement of Accounts
                    </b>
                  </div>
                  <div style={{ fontSize: 13, color: "rgba(255,255,255,0.85)", textShadow: "#1c1c1c 1px 1px 0px", paddingLeft: 2 }}>
                    BleST Tag Assignments Summary
                  </div>
                </div>
              </div>
              {filteredOrders.length > 0 && (
                <button
                  onClick={handleDownload}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    background: "#fff", border: "none", color: "#c13d00",
                    borderRadius: 30, padding: "10px 22px",
                    fontWeight: 700, fontSize: 14, cursor: "pointer",
                    boxShadow: "rgba(0,0,0,0.25) 0px 3px 8px", flexShrink: 0,
                  }}
                >
                  <i className="fa-solid fa-download" style={{ fontSize: 13 }} />
                  Download PDF
                </button>
              )}
            </div>
          </header>

          {/* Body */}
          <div
            style={{
              padding: "20px 24px",
              backgroundColor: "#f0f2f5",
              minHeight: "calc(100% - 76px)",
            }}
          >
            {loading ? (
              <div
                className="d-flex justify-content-center align-items-center"
                style={{ height: 220 }}
              >
                <div
                  className="spinner-border text-secondary"
                  role="status"
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            ) : err ? (
              <div className="alert alert-danger">{err}</div>
            ) : (
              <>
                {/* Summary cards */}
                <div className="row g-3 mb-4">
                  {[
                    {
                      label: "Total Batches",
                      value: hasFilters ? filteredOrders.length : orders.length,
                      sub: hasFilters ? `${orders.length} total` : null,
                    },
                    {
                      label: "Total Tags",
                      value: totalTags.toLocaleString(),
                      sub: null,
                    },
                    {
                      label: "Tag Assignments",
                      value: totalAssignments.toLocaleString(),
                      sub: null,
                    },
                    ...(!isBrandAdmin
                      ? [
                          {
                            label: "Clients",
                            value: allClients.length,
                            sub: null,
                          },
                        ]
                      : []),
                  ].map(({ label, value, sub }) => (
                    <div
                      className="col-6 col-md-3"
                      key={label}
                    >
                      <div
                        style={{
                          background: "white",
                          borderRadius: 10,
                          padding: "16px 20px",
                          boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                          height: "100%",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 11,
                            color: "#999",
                            textTransform: "uppercase",
                            letterSpacing: 0.8,
                            marginBottom: 6,
                          }}
                        >
                          {label}
                        </div>
                        <div
                          style={{
                            fontSize: 26,
                            fontWeight: 700,
                            color: "#1a1a1a",
                            lineHeight: 1,
                          }}
                        >
                          {value}
                        </div>
                        {sub && (
                          <div
                            style={{
                              fontSize: 11,
                              color: "#aaa",
                              marginTop: 4,
                            }}
                          >
                            {sub}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Filter trigger row */}
                <div style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  marginBottom: 16, flexWrap: "wrap",
                }}>
                  <button
                    onClick={() => setShowFiltersModal(true)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8,
                      background: "#fff", border: "1.5px solid #e5e7eb",
                      borderRadius: 10, padding: "8px 16px",
                      fontWeight: 700, fontSize: 13, color: "#374151",
                      cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                      transition: "border-color 0.15s",
                    }}
                  >
                    <i className="fas fa-sliders" style={{ color: activeFilterCount ? "#ff5100" : "#9ca3af", fontSize: 13 }} />
                    Filters
                    {activeFilterCount > 0 && (
                      <span style={{
                        background: "#ff5100", color: "#fff",
                        borderRadius: 9999, fontSize: 11, fontWeight: 800,
                        padding: "1px 7px", lineHeight: 1.6,
                      }}>
                        {activeFilterCount}
                      </span>
                    )}
                  </button>

                  {dateFrom && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "rgba(255,81,0,0.08)", border: "1px solid rgba(255,81,0,0.2)",
                      borderRadius: 9999, padding: "4px 10px", fontSize: 12, fontWeight: 700, color: "#c13d00",
                    }}>
                      From: {dateFrom}
                      <button onClick={() => setDateFrom("")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#c13d00", lineHeight: 1, fontSize: 12 }}>×</button>
                    </span>
                  )}
                  {dateTo && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "rgba(255,81,0,0.08)", border: "1px solid rgba(255,81,0,0.2)",
                      borderRadius: 9999, padding: "4px 10px", fontSize: 12, fontWeight: 700, color: "#c13d00",
                    }}>
                      To: {dateTo}
                      <button onClick={() => setDateTo("")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#c13d00", lineHeight: 1, fontSize: 12 }}>×</button>
                    </span>
                  )}
                  {filterTagType && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "rgba(255,81,0,0.08)", border: "1px solid rgba(255,81,0,0.2)",
                      borderRadius: 9999, padding: "4px 10px", fontSize: 12, fontWeight: 700, color: "#c13d00",
                    }}>
                      Type: {filterTagType}
                      <button onClick={() => setFilterTagType("")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#c13d00", lineHeight: 1, fontSize: 12 }}>×</button>
                    </span>
                  )}
                  {filterClient && (
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      background: "rgba(255,81,0,0.08)", border: "1px solid rgba(255,81,0,0.2)",
                      borderRadius: 9999, padding: "4px 10px", fontSize: 12, fontWeight: 700, color: "#c13d00",
                    }}>
                      Client: {filterClient}
                      <button onClick={() => setFilterClient("")} style={{ background: "none", border: "none", padding: 0, cursor: "pointer", color: "#c13d00", lineHeight: 1, fontSize: 12 }}>×</button>
                    </span>
                  )}
                  {hasFilters && (
                    <button
                      onClick={clearFilters}
                      style={{
                        background: "none", border: "none", color: "#dc2626",
                        fontWeight: 700, fontSize: 12, cursor: "pointer", padding: "4px 6px",
                      }}
                    >
                      Clear all
                    </button>
                  )}
                </div>

                {/* Statement document */}
                <div style={{ display: "flex", justifyContent: "center" }}>
                  {filteredOrders.length === 0 ? (
                    <div
                      style={{
                        padding: "48px 24px",
                        color: "#aaa",
                        textAlign: "center",
                        background: "white",
                        borderRadius: 10,
                        width: "100%",
                        maxWidth: "21cm",
                        boxShadow: "0 1px 4px rgba(0,0,0,0.07)",
                      }}
                    >
                      <i
                        className="fa-solid fa-file-circle-xmark"
                        style={{
                          fontSize: 40,
                          marginBottom: 12,
                          display: "block",
                        }}
                      />
                      No assignments match the current filters.
                    </div>
                  ) : (
                    <div
                      ref={statementRef}
                      style={{
                        width: "21cm",
                        minHeight: "29.7cm",
                        background: "white",
                        padding: "40px",
                        fontFamily: "Arial, sans-serif",
                        fontSize: "11pt",
                        color: "#333",
                        boxShadow: "0 2px 16px rgba(0,0,0,0.11)",
                        borderRadius: 4,
                      }}
                    >
                      {/* Logo */}
                      <div style={{ textAlign: "right", marginBottom: "30px" }}>
                        <img
                          src="/images/blest_logo.png"
                          alt="BleST Logo"
                          style={{ height: "80px" }}
                        />
                      </div>

                      {/* Title & Bill To */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "25px",
                        }}
                      >
                        <div>
                          <h4 style={{ marginBottom: "5px" }}>
                            Statement of Accounts
                          </h4>
                          <div style={{ fontSize: "10pt", color: "#555" }}>
                            Summary of BleST tag assignments.
                          </div>
                        </div>
                        <div style={{ textAlign: "right" }}>
                          {effectiveClientName && (
                            <div
                              style={{
                                marginBottom: 6,
                                fontSize: "10pt",
                                lineHeight: 1.6,
                              }}
                            >
                              <b>CLIENT:</b>
                              <br />
                              {effectiveClientName}
                              {clientOrgDetails?.address && (
                                <>
                                  <br />
                                  {clientOrgDetails.address}
                                </>
                              )}
                              {clientOrgDetails?.contactEmail && (
                                <>
                                  <br />
                                  {clientOrgDetails.contactEmail}
                                </>
                              )}
                              {clientOrgDetails?.primaryPhone && (
                                <>
                                  <br />
                                  {clientOrgDetails.primaryPhone}
                                </>
                              )}
                              {clientOrgDetails?.secondaryPhone && (
                                <>
                                  <br />
                                  {clientOrgDetails.secondaryPhone}
                                </>
                              )}
                            </div>
                          )}
                          <span style={{ fontSize: "10pt", color: "#777" }}>
                            Generated on {formatDate(new Date().toISOString())}
                          </span>
                        </div>
                      </div>

                      <hr style={{ margin: "10px 0 20px" }} />

                      {/* Summary */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          marginBottom: "15px",
                          fontSize: "10pt",
                        }}
                      >
                        <div>
                          <b>Total Batches:</b> {filteredOrders.length}
                          <br />
                          <b>Total Tags:</b> {totalTags.toLocaleString()}
                        </div>
                        <div style={{ textAlign: "right" }}>
                          <b>Total Assignments:</b>{" "}
                          {totalAssignments.toLocaleString()}
                        </div>
                      </div>

                      {/* Table */}
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "collapse",
                          marginTop: "10px",
                        }}
                      >
                        <thead>
                          <tr>
                            {[
                              "#",
                              "Date",
                              "Tag Type",
                              "Serial Range",
                              "Qty",
                              ...(!isBrandAdmin ? ["Client"] : []),
                              "Assignments",
                              "Unit Cost",
                              "Total",
                            ].map((h) => (
                              <th
                                key={h}
                                style={{
                                  border: "1px solid #ddd",
                                  padding: "6px",
                                  backgroundColor: "#f5f5f5",
                                  fontSize: "9pt",
                                }}
                              >
                                {h}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredOrders.map((order, index) => {
                            const qty = order.maxSerial - order.minSerial + 1;
                            return (
                              <tr key={order._id || index}>
                                <td style={cell}>{index + 1}</td>
                                <td style={cell}>
                                  {(order.date || "").split("T")[0]}
                                </td>
                                <td style={cell}>
                                  {order.type} ({order.length}mm)
                                </td>
                                <td style={cell}>
                                  {order.minSerial} - {order.maxSerial}
                                </td>
                                <td style={cellR}>{qty.toLocaleString()}</td>
                                {!isBrandAdmin && (
                                  <td style={cell}>
                                    {order.client?.name || "-"}
                                  </td>
                                )}
                                <td style={cellR}>
                                  {order.tagAssignments?.length || 0}
                                </td>
                                <td style={cellR}>
                                  {order.unitCost != null
                                    ? `${order.currency} ${fmtNum(order.unitCost)}`
                                    : "-"}
                                </td>
                                <td style={cellR}>
                                  {order.totalCost != null && order.totalCost !== 0
                                    ? `${order.currency} ${fmtNum(order.totalCost)}`
                                    : "-"}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>

                      {Object.keys(totalsByCurrency).length > 0 && (
                        <div style={{ textAlign: "right", marginTop: 12, fontSize: "10pt" }}>
                          {Object.entries(totalsByCurrency).map(([cur, total]) => (
                            <div key={cur} style={{ fontWeight: 700 }}>
                              Grand Total ({cur}): {cur} {fmtNum(total)}
                            </div>
                          ))}
                          <div style={{ fontSize: "8pt", color: "#999", marginTop: 4 }}>
                            Includes applicable discounts and tax.
                          </div>
                        </div>
                      )}

                      <hr style={{ margin: "30px 0" }} />

                      <div
                        style={{
                          textAlign: "right",
                          fontSize: "10pt",
                          color: "#555",
                        }}
                      >
                        <b>Bleep Security Tags Limited</b>
                        <br />
                        Plot 40, Nakasero Road, Kampala, Uganda
                        <br />
                        +256752781782 | +256414257661
                        <br />
                        fred@acnafrica.org | blest@bleep.ug
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <BlestFiltersModal
        show={showFiltersModal}
        handleClose={() => setShowFiltersModal(false)}
        dateFrom={dateFrom}
        dateTo={dateTo}
        filterTagType={filterTagType}
        filterClient={filterClient}
        allTagTypes={allTagTypes}
        allClients={allClients}
        isBrandAdmin={isBrandAdmin}
        onApply={handleApplyFilters}
        onClear={clearFilters}
      />
    </>
  );
};

export default BlestStatementOfAccounts;
